import React, { useEffect, useState } from 'react';
import { Modal } from '../shared/Modal';
import { performanceService } from '../../services/performanceService';
import { piAdminService } from '../../services/piAdminService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { formatPeriodLabel } from '../submissions/periodFormat';
import { ExpenseItemsTable } from '../submissions/ExpenseItemsTable';
import { toCsv, downloadCsv } from '../../utils/csv';
import { buildExpenseCsvRows, expenseCsvFilename } from '../../utils/expenseCsv';

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));

export function PerformanceSubmissionDetailModal({ submission, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [params, perf, expenses] = await Promise.all([
          piAdminService.getPIParameters(),
          performanceService.getSubmissionPerformance(submission.id),
          performanceService.getExpenseItems(submission.id),
        ]);
        if (!active) return;
        setExpenses(expenses || []);
        const byId = perf.reduce((m, r) => ({ ...m, [r.pi_parameter_id]: r.value }), {});
        const merged = [...params]
          .filter((p) => byId[p.id] != null)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((p) => ({ id: p.id, code: p.code, name: p.name, unit: p.unit, value: byId[p.id] }));
        setRows(merged);
      } catch (e) {
        if (active) setError(mapSupabaseError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [submission.id]);

  const valueOf = (code) => {
    const r = rows.find((x) => x.code === code);
    return r ? Number(r.value) : null;
  };
  const dep = valueOf('deposits');
  const wd = valueOf('withdrawals');
  const netDeposit = dep == null && wd == null ? null : (dep || 0) - (wd || 0);

  // PostgREST returns the pi_rewards embed as a single object (submission_id is
  // UNIQUE → one-to-one); tolerate an array shape too.
  const reward = (Array.isArray(submission.pi_rewards)
    ? submission.pi_rewards[0]
    : submission.pi_rewards) || null;
  const bd = reward?.breakdown || {};
  const bdTerms = Array.isArray(bd.terms)
    ? bd.terms.map((t) => ({
        key: t.metric,
        text: `${t.metric}: ${num(t.rate)} × ${num(t.value)} = ${num(t.contribution)}`,
      }))
    : [];

  return (
    <Modal onClose={onClose} role="dialog" aria-modal="true" aria-label="Performance submission detail">
      <h3>Performance submission</h3>
      <p className="form-label"><strong>{submission.submitter?.name}</strong> · {formatPeriodLabel(submission.period, submission.period_start, submission.period_end)}</p>
      {error && <div className="error-message" role="alert">{error}</div>}
      {loading ? <p className="loading-msg">Loading…</p> : (
        <table className="kpi-bundle-table">
          <thead><tr><th>Parameter</th><th>Value</th><th>Unit</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>{num(r.value)}</td>
                <td>{r.unit}</td>
              </tr>
            ))}
            <tr>
              <td><strong>Net deposit</strong></td>
              <td>{num(netDeposit)}</td>
              <td>USD</td>
            </tr>
          </tbody>
        </table>
      )}
      {!loading && expenses.length > 0 && (
        <>
          <ExpenseItemsTable rows={expenses} />
          <div className="modal-buttons">
            <button type="button" className="btn-secondary"
              onClick={() => downloadCsv(expenseCsvFilename(submission), toCsv(buildExpenseCsvRows(expenses)))}>
              Export expenses (CSV)
            </button>
          </div>
        </>
      )}
      {reward && (
        <section className="reward-section" aria-label="Reward">
          <h4>Reward</h4>
          <p className="form-label">
            <strong>{num(reward.amount)}</strong> USD · {reward.qualified ? 'Qualified' : 'Not qualified'}
          </p>
          {bd.gate_failed && <p className="form-label">Gate not met — reward is 0.</p>}
          {bd.payout_type && <p className="form-label">Payout: {bd.payout_type}</p>}
          {bd.base != null && <p className="form-label">Base: {num(bd.base)}</p>}
          {bdTerms.length > 0 && (
            <ul className="reward-breakdown">
              {bdTerms.map((t) => <li key={t.key}>{t.text}</li>)}
            </ul>
          )}
          {bd.tier_metric && (
            <p className="form-label">Tier ({bd.tier_metric} = {num(bd.tier_value)}): {num(bd.tier_amount)}</p>
          )}
          {bd.cap_applied && <p className="form-label">Cap applied.</p>}
        </section>
      )}
      <div className="modal-buttons">
        <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}
